import { useCallback, useEffect, useRef, useState } from 'react';

export interface TranscriptMessage {
  id?: number;
  call_sid: string;
  caller_name: string;
  transcript: string;
  confidence?: number | null;
  track: string;
  is_partial: boolean;
  created_at: string;
  _endTime?: string;
  _inlinePartial?: TranscriptMessage;
}

export interface CallGroup {
  caller_name: string;
  call_sid: string;
  transcripts: TranscriptMessage[];
  startTime: string;
  endTime: string;
  isLive: boolean;
}

interface PartialMap {
  [key: string]: TranscriptMessage;
}

const MAX_CALLS = 20;

export function useTranscriptions(enabled: boolean) {
  const [transcripts, setTranscripts] = useState<TranscriptMessage[]>([]);
  const [activePartials, setActivePartials] = useState<PartialMap>({});
  const [stoppedCalls, setStoppedCalls] = useState<Set<string>>(new Set());
  const [connected, setConnected] = useState(false);
  const channelRef = useRef<any>(null);

  // Subscribe to Pusher channel when enabled
  useEffect(() => {
    if (!enabled || !window.Echo) return;

    const channel = window.Echo.private('transcriptions');
    channelRef.current = channel;

    // Connection state tracking
    const pusher = (window.Echo as any).connector?.pusher;
    if (pusher) {
      pusher.connection.bind('connected', () => setConnected(true));
      pusher.connection.bind('disconnected', () => setConnected(false));
      if (pusher.connection.state === 'connected') {
        setConnected(true);
      }
    }

    // Final transcription results (persisted to DB)
    channel.listen('.App\\Events\\NewTranscriptionEvent', (event: any) => {
      const data: TranscriptMessage = event.data;
      const partialKey = `${data.call_sid}:${data.track}`;

      // Remove the active partial for this call+track
      setActivePartials((prev) => {
        const { [partialKey]: _, ...remaining } = prev;
        return remaining;
      });

      // Find the most recent transcript for this call AND same track.
      // Merge into it so each speaker's segments stay in one box,
      // even when both speakers talk simultaneously.
      setTranscripts((prev) => {
        const lastIdx = prev.findIndex(
          (t) => t.call_sid === data.call_sid && t.track === data.track
        );
        if (lastIdx !== -1) {
          const updated = [...prev];
          updated[lastIdx] = {
            ...updated[lastIdx],
            transcript: updated[lastIdx].transcript + ' ' + data.transcript,
            _endTime: data.created_at,
          };
          return updated;
        }
        return [data, ...prev];
      });
    });

    // Partial transcription results (live typing, not persisted)
    channel.listen('.App\\Events\\PartialTranscriptionEvent', (event: any) => {
      const data: TranscriptMessage = event.data;
      const partialKey = `${data.call_sid}:${data.track}`;

      setActivePartials((prev) => {
        // Only update if new text is longer (accumulated sentence, not fragment)
        const existing = prev[partialKey];
        if (existing && data.transcript.length < existing.transcript.length) {
          return prev;
        }
        return { ...prev, [partialKey]: data };
      });
    });

    // Call ended
    channel.listen('.App\\Events\\TranscriptionStoppedEvent', (event: any) => {
      const callSid = event.data?.call_sid || event.call_sid;

      // Remove all partials for this call
      setActivePartials((prev) => {
        const updated: PartialMap = {};
        for (const [key, partial] of Object.entries(prev)) {
          if (partial.call_sid !== callSid) {
            updated[key] = partial;
          }
        }
        return updated;
      });

      // Mark call as stopped
      setStoppedCalls((prev) => new Set(prev).add(callSid));
    });

    return () => {
      channel.stopListening('.App\\Events\\NewTranscriptionEvent');
      channel.stopListening('.App\\Events\\PartialTranscriptionEvent');
      channel.stopListening('.App\\Events\\TranscriptionStoppedEvent');
      window.Echo.leave('transcriptions');
      channelRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  // Build grouped calls from transcripts + active partials
  const callGroups: CallGroup[] = buildCallGroups(
    transcripts,
    activePartials,
    stoppedCalls
  );

  // Evict oldest ended calls beyond MAX_CALLS
  const liveCalls = callGroups.filter((g) => g.isLive);
  const endedCalls = callGroups.filter((g) => !g.isLive);
  const visibleCalls = [
    ...liveCalls,
    ...endedCalls.slice(0, MAX_CALLS - liveCalls.length),
  ];

  const clearAll = useCallback(() => {
    setTranscripts([]);
    setActivePartials({});
    setStoppedCalls(new Set());
  }, []);

  return {
    callGroups: visibleCalls,
    connected,
    clearAll,
  };
}

function buildCallGroups(
  transcripts: TranscriptMessage[],
  activePartials: PartialMap,
  stoppedCalls: Set<string>
): CallGroup[] {
  const groups: Record<string, CallGroup> = {};

  // Build groups from finalized transcripts
  transcripts.forEach((t) => {
    const callSid = t.call_sid || 'unknown';
    if (!groups[callSid]) {
      groups[callSid] = {
        caller_name: t.caller_name,
        call_sid: callSid,
        transcripts: [],
        startTime: t.created_at,
        endTime: t.created_at,
        isLive: !stoppedCalls.has(callSid),
      };
    }

    groups[callSid].transcripts.push(t);

    if (new Date(t.created_at) < new Date(groups[callSid].startTime)) {
      groups[callSid].startTime = t.created_at;
    }
    const effectiveEnd = (t as any)._endTime || t.created_at;
    if (new Date(effectiveEnd) > new Date(groups[callSid].endTime)) {
      groups[callSid].endTime = effectiveEnd;
    }
  });

  // Ensure groups exist for active partials even if no finals yet
  Object.values(activePartials).forEach((partial) => {
    const callSid = partial.call_sid || 'unknown';
    if (!groups[callSid]) {
      groups[callSid] = {
        caller_name: partial.caller_name,
        call_sid: callSid,
        transcripts: [],
        startTime: partial.created_at,
        endTime: partial.created_at,
        isLive: true,
      };
    }
  });

  // Sort groups by most recent activity (newest first)
  const sorted = Object.values(groups).sort(
    (a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime()
  );

  // Sort transcripts within each group chronologically (oldest first)
  sorted.forEach((group) => {
    group.transcripts.sort((a, b) => {
      const timeDiff =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (timeDiff !== 0) return timeDiff;
      return (a.id || 0) - (b.id || 0);
    });
  });

  // AFTER sorting: inject partials inline with the chronologically last
  // same-speaker transcript, or as standalone entries for different speakers
  Object.values(activePartials).forEach((partial) => {
    const callSid = partial.call_sid || 'unknown';
    const group = sorted.find((g) => g.call_sid === callSid);
    if (!group) return;

    const lastTranscript =
      group.transcripts.length > 0
        ? group.transcripts[group.transcripts.length - 1]
        : null;

    if (lastTranscript && !lastTranscript.is_partial && lastTranscript.track === partial.track) {
      // Same speaker as chronologically last finalized — show inline
      lastTranscript._inlinePartial = partial;
    } else {
      // Different speaker or no finals yet — standalone partial entry
      group.transcripts.push({
        ...partial,
        is_partial: true,
      });
    }
    group.isLive = true;
  });

  return sorted;
}

export function formatTrack(track: string): string {
  if (track === 'inbound_track') return 'Caller';
  if (track === 'outbound_track') return 'Agent';
  return track || 'Unknown';
}

export function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
